import { DesktopDatePicker, LocalizationProvider } from '@ori-ui/date-pickers';
import { InfoCircle } from '@ori-ui/icons';
import type { TextFieldProps } from '@ori-ui/mui';
import { Box, FormControl, FormHelperText, Grid, styled } from '@ori-ui/mui';
import DateAdapter from '@ori-ui/mui/DateAdapter';
import { useCulture } from '@ori/culture';
import type { GlobalSharedMetadataValidationDateTimeValidationDto } from '@ori/global-api-client-react';
import useTestId from '@ori/testid-generator';
import { format } from 'date-fns';
import type { FC, ReactElement } from 'react';
import type { ControllerFieldState } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import { Localization } from '../../../Localization';
import { useFormError } from '../../../contexts';
import { useTranslations } from '../../../hooks/useTranslations_simplified-registration-BrandPartner';
import { TranslationKey } from '../../../models';
import { generateDateFormat, getErrorLocalizationByCode, renderValidationMessage } from '../../../utils';

interface IDatePickerFieldProps {
  name: string;
  label?: ReactElement | string;
  infoText?: ReactElement | string;
  placeholderText?: string;
  isEditable?: boolean;
  isAvailable?: boolean;
  displayOrder?: number;
  validation?: GlobalSharedMetadataValidationDateTimeValidationDto;
  fieldState?: ControllerFieldState;
  defaultDate?: Date;
  invalidMsg: TranslationKey;
}

const StyledInfoBox = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  width: '100%',
  fontSize: '12px',
  marginTop: theme.spacing(2),
  color: theme.palette.grey.A400,
}));

const StyledInfoCircle = styled(InfoCircle)(({ theme }) => ({
  fontSize: '30px',
  marginRight: theme.spacing(2),
}));

const isValidDate = (d?: Date) =>
  // eslint-disable-next-line unicorn/prefer-number-properties, no-restricted-globals --- we needed this solution
  d instanceof Date && !isNaN(d as unknown as number);

const isValidMax = (d?: Date, max?: Date) => d && max && d.getTime() < max.getTime();

const isValidMin = (d?: Date, min?: Date) => d && min && d.getTime() > min.getTime();

export const DatePickerField: FC<IDatePickerFieldProps> = (props) => {
  const { name, label, infoText, isEditable, isAvailable, displayOrder, validation, defaultDate, invalidMsg } = props;

  const { getTestIdProps, getTestId } = useTestId();

  const { culture } = useCulture();
  const { formatMessage } = useTranslations();
  const culturePattern = culture?.calendarFormat?.patterns?.shortDatePattern ?? '';

  const dateFormat = generateDateFormat(culturePattern);

  const {
    context: { errorData },
  } = useFormError();
  const { customErrors } = errorData;

  const submitError = customErrors?.find((error) => error.fieldName?.toLowerCase() === name.toLowerCase());

  const errorMessage = getErrorLocalizationByCode(submitError?.errorCode, name);

  const invalidDateMessage = formatMessage(invalidMsg, {
    correctFormat: dateFormat.toUpperCase(),
  });

  const actualDate = new Date();
  actualDate.setFullYear(actualDate.getFullYear() - 18);

  const minDate = validation?.minValue ? new Date(validation.minValue) : undefined;
  const maxDate = validation?.maxValue ? new Date(validation.maxValue) : undefined;

  const formattedMax = maxDate ? format(maxDate, culturePattern).replaceAll(' ', '') : null;
  const formattedMin = minDate ? format(minDate, culturePattern).replaceAll(' ', '') : null;

  const invalidMaxDateMessage = formatMessage(TranslationKey.validationMaxValue, {
    max: formattedMax,
  });

  const invalidMinDateMessage = formatMessage(TranslationKey.validationMinValue, {
    min: formattedMin,
  });

  const initialDate = defaultDate ? new Date(defaultDate) : actualDate;

  // we need start tabindex from 1 but displayOrder starts from 0
  const correctTabIndex = typeof displayOrder === 'number' ? displayOrder + 1 : displayOrder;

  const renderInputProps = (fieldState: ControllerFieldState): TextFieldProps => ({
    fullWidth: true,
    variant: 'outlined',
    disabled: !isEditable,
    required: validation?.isMandatory,
    error: !!fieldState.error,
    inputProps: {
      tabIndex: correctTabIndex,
      'data-testid': getTestId(`${name}-date-field`),
      maxLength: 10, // hardcoded for HR where we hawe this strange form dd.mm.yyyy. and for picker is possible this value 09.09.2000.0
    },
    helperText: fieldState.error ? renderValidationMessage(fieldState.error) : null,
  });

  const renderField = () => (
    <Grid
      item
      xs={12}
      sm={12}
      style={{ order: displayOrder }}
    >
      <Box
        margin="0 auto 25px"
        maxWidth={500}
        textAlign="left"
      >
        <Controller
          name={name}
          rules={{
            required: validation?.isMandatory,
            validate: (currentDate?: Date) => {
              if (currentDate && !isValidDate(currentDate)) {
                return invalidDateMessage;
              }
              if (currentDate && maxDate && !isValidMax(currentDate, maxDate)) {
                return invalidMaxDateMessage;
              }
              if (currentDate && minDate && !isValidMin(currentDate, minDate)) {
                return invalidMinDateMessage;
              }

              return true;
            },
          }}
          defaultValue={initialDate}
          render={({ field, fieldState }) => (
            <LocalizationProvider dateAdapter={DateAdapter}>
              <FormControl fullWidth>
                <DesktopDatePicker
                  disableOpenPicker
                  open={false}
                  format={dateFormat}
                  label={label}
                  minDate={minDate}
                  maxDate={maxDate}
                  disabled={!isEditable}
                  {...field}
                  slotProps={{
                    textField: { ...renderInputProps(fieldState), onBlur: field.onBlur },
                  }}
                />
                {submitError ? (
                  <FormHelperText error>
                    <Localization
                      {...errorMessage}
                      values={{ max: maxDate }}
                    />
                  </FormHelperText>
                ) : null}
              </FormControl>
            </LocalizationProvider>
          )}
        />
        {infoText ? (
          <StyledInfoBox {...getTestIdProps(`${name}-date-field-info-text`)}>
            <StyledInfoCircle />
            {infoText}
          </StyledInfoBox>
        ) : null}
      </Box>
    </Grid>
  );

  const fieldComponent = isAvailable && renderField();

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment -- we need fragment
    <>{fieldComponent}</>
  );
};
